import React from "react";
import Moment from "moment";
import { extendMoment } from "moment-range";
import {
  Card,
  CardText,
  CardHeader,
  Paper,
  Slider,
  RaisedButton,
} from "material-ui";
import _ from "lodash";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import DateRange from "../../DateRangeComponent";
import styles from "../styles.scss";

const moment = extendMoment(Moment);
const DateFormat = "DDMMM";

export default class SalesReport extends React.Component {
  static propTypes = {
    trips: PropTypes.arrayOf(PropTypes.object).isRequired,
  };

  constructor(props) {
    super(props);

    const defaultStart = moment().day(-6).startOf("day");
    const defaultEnd = moment().day(1).startOf("day");

    this.state = {
      startDate: defaultStart,
      endDate: defaultEnd,
      enteredStart: defaultStart,
      enteredEnd: defaultEnd,
      upcomingTripDays: 30,
    };
  }

  updateDates = (e) => {
    const { startDate, endDate } = e;
    this.setState({
      enteredStart: startDate,
      enteredEnd: endDate,
    });
  };

  testDate = (date) => {
    const { startDate, endDate } = this.state;
    const momentDate = moment.isMoment(date) ? date : moment(date);

    return startDate <= momentDate && momentDate < endDate;
  };

  updateSlider = (e, value) => {
    this.setState({ upcomingTripDays: value });
  };

  setDates = () => {
    const { enteredStart, enteredEnd } = this.state;
    this.setState({
      startDate: enteredStart,
      endDate: enteredEnd,
    });
  };

  render() {
    const { trips } = this.props;
    const {
      startDate,
      endDate,
      upcomingTripDays,
      enteredStart,
      enteredEnd,
    } = this.state;
    const formattedStartDate = startDate.format(DateFormat).toUpperCase();
    const formattedEndDate = endDate.format(DateFormat).toUpperCase();
    const duration = moment.range(startDate, endDate).diff("days");
    const dateString = `${formattedStartDate}-${duration}NT`;

    const tripsByWeek = {};
    _.each(trips, (trip) => {
      const { created } = trip;
      const momentCreated = moment(created);
      const tripWeek = `${momentCreated.isoWeek()}${momentCreated.year()}`;

      tripsByWeek[tripWeek] = (tripsByWeek[tripWeek] || 0) + 1;
    });
    const tripsCreated =
      tripsByWeek[`${startDate.isoWeek()}${startDate.year()}`];

    const counts = _.values(tripsByWeek);
    const avgTripsCreated = +(
      _.reduce(counts, (memo, count) => memo + count) / counts.length
    ).toFixed(1);

    const prospects = {};
    let numProspects = 0;
    _.each(
      trips.filter(
        (trip) =>
          !trip.cancelled &&
          trip.prospect &&
          !trip.specificDates &&
          trip.segments.length === 0
      ),
      (trip) => {
        const { ownerName } = trip;
        const ownerList = prospects[ownerName] || [];
        prospects[ownerName] = [...ownerList, trip];
        numProspects += 1;
      }
    );

    const needsBooking = trips.filter(
      (trip) =>
        !trip.cancelled &&
        _.some(
          trip.segments,
          (segment) => !segment.booked && !segment.cancelled
        )
    );

    const tripsVoided = trips.filter(({ cancelledTimestamp }) =>
      this.testDate(cancelledTimestamp)
    );

    const tripsByInbox = {
      "team@": 0,
      "insidecircle@": 0,
    };
    _.each(trips, (trip) => {
      const { created, inbox } = trip;

      if (this.testDate(created)) {
        tripsByInbox[inbox] += 1;
      }
    });

    const segmentFacts = {
      bookings: 0,
      cancellations: 0,
      lastMinute: 0,
    };
    const lastMinuteTrips = {};
    _.each(trips, (trip) => {
      const { segments, created, travelStartDate, cancelled } = trip;
      if (travelStartDate !== null && !cancelled) {
        const momentStartDate = moment(travelStartDate);
        const cutOff = moment(created).add(7, "days");

        if (momentStartDate >= moment() && momentStartDate <= cutOff) {
          segmentFacts.lastMinute += 1;

          const { ownerName } = trip;
          const ownerList = lastMinuteTrips[ownerName] || [];
          lastMinuteTrips[ownerName] = [...ownerList, trip];
        }
      }

      _.each(segments, ({ bookedTimestamp, cancelledTimestamp }) => {
        if (this.testDate(bookedTimestamp)) {
          segmentFacts.bookings += 1;
        }

        if (this.testDate(cancelledTimestamp)) {
          segmentFacts.cancellations += 1;
        }
      });
    });

    const tripsByYear = {};
    _.each(
      trips.filter(
        ({ travelStartDate, cancelled }) =>
          !cancelled &&
          travelStartDate !== null &&
          moment(travelStartDate) > moment()
      ),
      ({ travelStartDate }) => {
        const momentStartDate = moment(travelStartDate);
        const year = momentStartDate.year();
        const month = momentStartDate.format("MMMM");

        const yearObj = tripsByYear[year] || {};
        const monthCount = yearObj[month] || 0;

        yearObj[month] = monthCount + 1;
        tripsByYear[year] = yearObj;
      }
    );

    const futureTripCounts = {};
    _.each(_.toPairs(tripsByYear), ([year, months]) => {
      futureTripCounts[year] = _.toPairs(months).map(
        ([month, count], index) => {
          const key = `${year}.${index}`;
          return (
            <Paper key={key} className={styles.futureTravelBox}>
              {month} {year}: {count} trips
            </Paper>
          );
        }
      );
    });

    const tooLate = {};
    let numTooLate = 0;
    _.each(
      needsBooking.filter(
        ({ travelStartDate }) => moment(travelStartDate) < moment()
      ),
      (trip) => {
        const { ownerName } = trip;
        const ownerList = tooLate[ownerName] || [];
        tooLate[ownerName] = [...ownerList, trip];
        numTooLate += 1;
      }
    );

    const upcoming = {};
    let numUpcoming = 0;
    _.each(
      needsBooking.filter(
        ({ travelStartDate }) =>
          moment(travelStartDate) < moment().add(upcomingTripDays, "days")
      ),
      (trip) => {
        const { ownerName } = trip;
        const ownerList = upcoming[ownerName] || [];
        upcoming[ownerName] = [...ownerList, trip];
        numUpcoming += 1;
      }
    );

    const tbdTrips = {};
    let numTbd = 0;
    _.each(
      needsBooking.filter(({ travelStartDate }) => travelStartDate === null),
      (trip) => {
        const { ownerName } = trip;
        const ownerList = tbdTrips[ownerName] || [];
        tbdTrips[ownerName] = [...ownerList, trip];
        numTbd += 1;
      }
    );

    return (
      <Card className={styles.report}>
        <CardHeader title={`Sales Report for ${dateString}`} />
        <CardText className={styles.settings}>
          <DateRange
            startValue={formattedStartDate}
            endValue={formattedEndDate}
            startLabel="Start Date"
            endLabel="End Date"
            onChange={this.updateDates}
            allowPast
          />
          <RaisedButton
            primary
            label="Set Dates"
            onTouchTap={this.setDates}
            disabled={startDate === enteredStart || endDate === enteredEnd}
          />
        </CardText>
        <CardText>
          <div className={styles.summaryContainer}>
            <Paper className={styles.summaryBox}>
              <span>
                Trips created: {tripsCreated} (average {avgTripsCreated})
              </span>
              <span>Trips needing booking: {needsBooking.length}</span>
              <span>Trips voided: {tripsVoided.length}</span>
            </Paper>
            <Paper className={styles.summaryBox}>
              <span>Trips from team@ inbox: {tripsByInbox["team@"]}</span>
              <span>
                Trips from insidecircle@ inbox: {tripsByInbox["insidecircle@"]}
              </span>
            </Paper>
            <Paper className={styles.summaryBox}>
              <span>Bookings made: {segmentFacts.bookings}</span>
              <span>Cancellations made: {segmentFacts.cancellations}</span>
              <span>
                Inquiries added w/in 7 days of travel: {segmentFacts.lastMinute}
              </span>
            </Paper>
          </div>
          <h3>Trips with Future Travel</h3>
          {_.toPairs(futureTripCounts).map(([year, months]) => (
            <div key={year}>
              <h4>{year}</h4>
              <div className={styles.futureTravelContainer}>{months}</div>
            </div>
          ))}
          <h3>{numTooLate} trips too late to book:</h3>
          <div className={styles.splitList}>
            {_.toPairs(tooLate).map(([owner, ownedTrips]) => (
              <div key={owner}>
                <h4>
                  {owner} ({ownedTrips.length} trips)
                </h4>
                <ul>
                  {ownedTrips.map((trip) => {
                    const { id, tripName } = trip;
                    return (
                      <li key={id}>
                        <Link to={`/trips/${id}/`}>{tripName}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <h3>
            {numUpcoming} unbooked trips traveling in next {upcomingTripDays}{" "}
            days:
          </h3>
          <Slider
            min={7}
            max={365}
            step={1}
            onChange={_.debounce(this.updateSlider, 100)}
            value={upcomingTripDays}
          />
          <div className={styles.splitList}>
            {_.toPairs(upcoming).map(([owner, ownedTrips]) => (
              <div key={owner}>
                <h4>
                  {owner} ({ownedTrips.length} trips)
                </h4>
                <ul>
                  {ownedTrips.map((trip) => {
                    const { id, tripName } = trip;
                    return (
                      <li key={id}>
                        <Link to={`/trips/${id}/`}>{tripName}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <h3>
            {segmentFacts.lastMinute} inquiries added w/in 7 days of travel:
          </h3>
          <div className={styles.splitList}>
            {_.toPairs(lastMinuteTrips).map(([owner, ownedTrips]) => (
              <div key={owner}>
                <h4>
                  {owner} ({ownedTrips.length} trips)
                </h4>
                <ul>
                  {ownedTrips.map((trip) => {
                    const { id, tripName } = trip;
                    return (
                      <li key={id}>
                        <Link to={`/trips/${id}/`}>{tripName}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <h3>{numTbd} trips without travel dates:</h3>
          <div className={styles.splitList}>
            {_.toPairs(tbdTrips).map(([owner, ownedTrips]) => (
              <div key={owner}>
                <h4>
                  {owner} ({ownedTrips.length} trips)
                </h4>
                <ul>
                  {ownedTrips.map((trip) => {
                    const { id, tripName } = trip;
                    return (
                      <li key={id}>
                        <Link to={`/trips/${id}/`}>{tripName}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <h3>{numProspects} sales prospects:</h3>
          <div className={styles.splitList}>
            {_.toPairs(prospects).map(([owner, ownedTrips]) => (
              <div key={owner}>
                <h4>
                  {owner} ({ownedTrips.length} trips)
                </h4>
                <ul>
                  {ownedTrips.map((trip) => {
                    const { id, tripName } = trip;
                    return (
                      <li key={id}>
                        <Link to={`/trips/${id}/`}>{tripName}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </CardText>
      </Card>
    );
  }
}



// WEBPACK FOOTER //
// /src/components/Reports/Sales/SalesReport.js